/**
 * Violation Service
 * Handles all violation-related API calls
 */

import api from './api';
import { VIOLATION_ENDPOINTS, MEDIA_SELECTOR } from './endpoints';

/**
 * Fetch violation data
 * @returns {Promise<Object>} Violation data
 */
export const getViolation = async () => {
  try {
    const response = await api.get(VIOLATION_ENDPOINTS.GET_VIOLATION);
    return response.data;
  } catch (error) {
    throw {
      status: error.response?.status,
      message: error.response?.data || error.message,
      originalError: error,
    };
  }
};

/**
 * Fetch a single violation image
 * @param {string} ticketNumber - Ticket number
 * @param {number} imageIndex - Image index (1-based)
 * @param {number} mediaSelector - Media selector (1 = thumbnail, 2 = full image)
 * @returns {Promise<Blob>} Image blob
 */
export const getViolationImage = async (ticketNumber, imageIndex, mediaSelector = MEDIA_SELECTOR.FULL_IMAGE) => {
  const url = VIOLATION_ENDPOINTS.GET_IMAGES(ticketNumber, imageIndex, mediaSelector);
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      const response = await api.get(url, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      if (attempt === 2) {
        console.warn(`Image ${imageIndex} could not be loaded after 2 attempts, skipping.`);
        return null;
      }
    }
  }
};

/**
 * Fetch multiple violation images
 * @param {string} ticketNumber - Ticket number
 * @param {number} numImages - Number of images to fetch
 * @param {number} mediaSelector - Media selector (1 = thumbnail, 2 = full image)
 * @returns {Promise<Array<string>>} Array of image object URLs
 */
export const getViolationImages = async (ticketNumber, numImages, mediaSelector = MEDIA_SELECTOR.FULL_IMAGE) => {
  try {
    const imageUrls = [];
    const requests = [];

    for (let i = 1; i <= numImages; i++) {
      const request = getViolationImage(ticketNumber, i, mediaSelector)
        .then((blob) => {
          if (!blob) return;
          const imageBlob = new Blob([blob], { type: 'image/png' });
          const imageUrl = URL.createObjectURL(imageBlob);
          imageUrls.push(imageUrl);
        });

      requests.push(request);
    }

    await Promise.all(requests);
    return imageUrls;
  } catch (error) {
    throw {
      status: error.response?.status,
      message: 'Failed to load violation images',
      originalError: error,
    };
  }
};

/**
 * Send violation information via email
 * @param {string} email - Recipient email address
 * @returns {Promise<Object>} Response data
 */
export const sendViolationEmail = async (email) => {
  try {
    const response = await api.post(VIOLATION_ENDPOINTS.SEND_MAIL, {
      mail: email,
    });
    return response.data;
  } catch (error) {
    throw {
      status: error.response?.status,
      message: error.response?.data || 'Failed to send email',
      originalError: error,
    };
  }
};
